import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Tabs,
  Tab,
  IconButton,
  Grid,
  Divider,
  Link,
  CircularProgress,
  useMediaQuery,
  useTheme as useMuiTheme,
  Chip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  WhatsApp as WhatsAppIcon,
  Public as PublicIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  EventBusy as EventBusyIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useCompetencia } from '../context/CompetenciaContext';
import { EventProvider } from '../context/EventContext';
import { ThemeProvider } from '@mui/material/styles';
import { getTheme } from '../theme';
import CompetitionCoverImage from '../components/Common/CompetitionCoverImage';
import CompetitionLogoImage from '../components/Common/CompetitionLogoImage';
import Convocatoria from '../pages/EventPages/Convocatoria';
import RegistroForm from './EventPages/RegistroForm';
import EventsTable from '../components/Eventos/EventsTable';

// ** NUEVO IMPORT **
import { getRegistrationStatus } from '../utils/registrationStatusUtils';

const EventPage = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { slug } = useParams();
  const { competencias, loading: loadingCompetencias } = useCompetencia();
  const [competencia, setCompetencia] = useState(null);
  const [theme, setTheme] = useState(null);
  
  // Hook para detectar tamaño de pantalla
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));

  useEffect(() => {
    if (!loadingCompetencias && competencias.length > 0 && slug) {
      const compEncontrada = competencias.find(c => c.slug === slug);
      if (compEncontrada) {
        setCompetencia(compEncontrada);
        const primaryColor = compEncontrada?.theme?.colorPrimario ?? '#1976d2';
        const secondaryColor = compEncontrada?.theme?.colorSecundario ?? '#dc004e';
        try {
          const dynamicTheme = getTheme(primaryColor, secondaryColor);
          setTheme(dynamicTheme);
        } catch(error) {
          console.error("Error creando el tema:", error);
          setTheme(getTheme('#1976d2', '#dc004e'));
        }
      } else {
        console.warn(`No se encontró competencia con slug: ${slug}`);
        setCompetencia(null);
        setTheme(null);
      }
    } else if (!loadingCompetencias) {
      setCompetencia(null);
      setTheme(null);
    }
  }, [slug, competencias, loadingCompetencias]);

  // ** NUEVA LÓGICA: Obtener estado de inscripciones **
  const registrationStatus = competencia ? getRegistrationStatus(
    competencia.general?.fechaInicioInscripciones,
    competencia.general?.fechaFinInscripciones
  ) : { canRegister: true, status: 'open' };

  // ** FUNCIÓN PARA OBTENER EL LABEL Y PROPIEDADES DE LA TAB DE REGISTRO **
  const getRegistrationTabConfig = () => {
    const baseLabel = 'Registro';
    
    switch (registrationStatus.status) {
      case 'not-started':
        return {
          label: baseLabel,
          icon: <ScheduleIcon sx={{ fontSize: 16, ml: 0.5 }} />,
          chip: {
            label: 'Próximamente',
            color: 'info',
            size: 'small',
          },
          sx: {
            position: 'relative',
            '& .MuiTab-iconWrapper': {
              marginBottom: '0 !important',
              marginLeft: '4px',
            }
          }
        };
      case 'closed':
        return {
          label: baseLabel,
          icon: <EventBusyIcon sx={{ fontSize: 16, ml: 0.5 }} />,
          chip: {
            label: 'Cerrado',
            color: 'error',
            size: 'small',
          },
          sx: {
            position: 'relative',
            color: theme?.palette.text.secondary,
            '& .MuiTab-iconWrapper': {
              marginBottom: '0 !important',
              marginLeft: '4px',
            }
          }
        };
      case 'open':
      default:
        return {
          label: baseLabel,
          icon: <CheckCircleIcon sx={{ fontSize: 16, ml: 0.5 }} />,
          chip: {
            label: 'Abierto',
            color: 'success',
            size: 'small',
          },
          sx: {
            position: 'relative',
            '& .MuiTab-iconWrapper': {
              marginBottom: '0 !important',
              marginLeft: '4px',
            }
          }
        };
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Fechas no disponibles';
    try {
      const options = { day: '2-digit', month: 'long' };
      const yearOptions = { year: 'numeric' };
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Fechas inválidas';

      const startStr = start.toLocaleDateString('es-ES', options);
      const endStr = end.toLocaleDateString('es-ES', options);
      const yearStr = end.toLocaleDateString('es-ES', yearOptions);

      if (start.toDateString() === end.toDateString()) {
        return `${startStr}, ${yearStr}`;
      }
      
      if (start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear()) {
        return `${start.getUTCDate()}-${end.getUTCDate()} de ${start.toLocaleDateString('es-ES', { month: 'long'})}, ${yearStr}`;
      }

      return `${startStr} - ${endStr}, ${yearStr}`;
    } catch(e) {
      console.warn("Error formateando rango de fecha:", startDate, endDate, e);
      return 'Fechas no disponibles';
    }
  };

  if (loadingCompetencias) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
  }
  
  if (!competencia) {
    return <Typography variant="h6" align="center" sx={{ p: 5 }}>Competencia no encontrada.</Typography>;
  }
  
  if (!theme) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /><Typography sx={{ml:2}}>Generando tema...</Typography></Box>;
  }

  // Acceso a Datos Anidados
  const nombre = competencia?.general?.nombre ?? 'Competencia';
  const subtitulo = competencia?.general?.subtitulo;
  const logoUrl = competencia?.general?.logo;
  const imagenPortadaUrl = competencia?.general?.imagenPortada;
  const fechaInicio = competencia?.general?.fechaInicioCompetencia;
  const fechaFin = competencia?.general?.fechaFinCompetencia;
  const lugar = competencia?.location?.lugar ?? 'No especificado';
  const direccion = competencia?.location?.direccion;
  const mapShortLink = competencia?.location?.mapShortLink;
  const mapEmbedLink = competencia?.location?.mapEmbedLink;
  const contactos = competencia?.contact?.contactos || [];
  const facebookLink = competencia?.social?.facebook;
  const instagramLink = competencia?.social?.instagram;
  const twitterLink = competencia?.social?.twitter;
  const whatsappLink = competencia?.social?.whatsapp;
  const sitioWebLink = competencia?.social?.sitioWeb;

  const competitionId = competencia?.id;
  const registrationTabConfig = getRegistrationTabConfig();

  const redesSociales = [
    { name: 'facebook', icon: <FacebookIcon />, link: facebookLink },
    { name: 'instagram', icon: <InstagramIcon />, link: instagramLink },
    { name: 'twitter', icon: <TwitterIcon />, link: twitterLink },
    { name: 'whatsapp', icon: <WhatsAppIcon />, link: whatsappLink },
    { name: 'web', icon: <PublicIcon />, link: sitioWebLink },
  ].filter(red => red.link);

  return (
    <ThemeProvider theme={theme}>
      <Box>
        {/* 🖼️ IMAGEN DE PORTADA MEJORADA CON FALLBACK AUTOMÁTICO */}
        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
            <CompetitionCoverImage
              src={imagenPortadaUrl}
              alt={nombre}
              height={{ xs: 200, sm: 250, md: 320 }}
              width="100%"
              competitionName={nombre}
              borderRadius="0"
              sx={{
                '& img': {
                  transition: 'transform 0.3s ease',
                },
                '&:hover img': {
                  transform: 'scale(1.02)',
                }
              }}
            />
          
          {/* Overlay gradient para mejor legibilidad */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '40%',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
              pointerEvents: 'none',
            }}
          />
        </Box>

        {/* ENCABEZADO CON DISEÑO RESPONSIVO */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          px={{ xs: 2, sm: 3, md: 4 }}
          py={{ xs: 2, md: 2 }}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
            {/* 🏆 LOGO CON FALLBACK AUTOMÁTICO */}
            <Box
              sx={{
                width: { xs: 70, sm: 85, md: 100 },
                height: { xs: 70, sm: 85, md: 100 },
                border: '3px solid white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              {logoUrl ? (
                // Si hay logo, usar Avatar tradicional
                <Avatar
                  src={logoUrl}
                  variant="rounded"
                  alt="Logo del Evento"
                  sx={{ 
                    width: '100%',
                    height: '100%',
                    '& img': {
                      objectFit: 'contain',
                    }
                  }}
                />
              ) : (
                // Si no hay logo, usar CompetitionLogoImage
                <CompetitionLogoImage
                  src={logoUrl}
                  alt="Logo del Evento"
                  width="100%"
                  height="100%"
                  competitionName={nombre}
                  borderRadius="0"
                />
              )}
            </Box>
            
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
              <Typography 
                variant="h4" 
                fontWeight="bold"
                sx={{ 
                  fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.125rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                {nombre}
              </Typography>
              {subtitulo && (
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    opacity: 0.95,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  }}
                >
                  {subtitulo}
                </Typography>
              )}
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={{ xs: 1, sm: 2 }}
                mt={0.5}
                width="100%"
              >
                {fechaInicio && fechaFin && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarIcon fontSize={isMobile ? "small" : "medium"} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      }}
                    >
                      {formatDateRange(fechaInicio, fechaFin)}
                    </Typography>
                  </Box>
                )}
                {lugar && lugar !== 'No especificado' && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationIcon fontSize={isMobile ? "small" : "medium"} />
                    {mapShortLink ? (
                      <Link 
                        color="inherit" 
                        href={mapShortLink} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        underline="hover"
                        sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          '&:hover': {
                            textDecoration: 'underline',
                            opacity: 0.8,
                          }
                        }}
                      >
                        {lugar}
                      </Link>
                    ) : (
                      <Typography 
                        variant="body2"
                        sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        }}
                      >
                        {lugar}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              
              {/* ** NUEVO: Indicador de estado de inscripciones en el header ** */}
              {!registrationStatus.canRegister && (
                <Box mt={1}>
                  <Chip
                    icon={registrationTabConfig.icon}
                    label={`Inscripciones ${registrationTabConfig.chip.label}`}
                    color={registrationTabConfig.chip.color}
                    size="small"
                    variant="filled"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      fontWeight: 'medium',
                      '& .MuiChip-icon': {
                        color: 'white',
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Iconos de redes sociales mejorados */}
          {redesSociales.length > 0 && (
            <Box 
              display="flex" 
              gap={1} 
              mt={{ xs: 2, md: 0 }}
              flexWrap="wrap"
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              width={{ xs: '100%', md: 'auto' }}
            >
              {redesSociales.map((red) => (
                <IconButton
                  key={red.name}
                  href={red.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    color: theme.palette.primary.contrastText, 
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': { 
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  aria-label={red.name}
                >
                  {red.icon}
                </IconButton>
              ))}
            </Box>
          )}
        </Box>

        {/* CONTENIDO PRINCIPAL (Grid) - Adaptativo a dispositivos */}
        <Grid container spacing={{ xs: 2, md: 3 }} p={{ xs: 2, sm: 2, md: 3 }}>
          {/* Columna Izquierda (Detalles) - Orden cambia en móviles */}
          <Grid item xs={12} md={4} order={{ xs: 2, md: 1 }}>
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                p: { xs: 1.5, sm: 2 },
                mb: 2,
              }}
            >
              <Typography 
                variant="h6" 
                fontWeight="medium" 
                gutterBottom 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                <LocationIcon fontSize={isMobile ? "small" : "medium"} color="action" /> Lugar
              </Typography>
              <Divider sx={{ mb: 1.5 }}/>
              <Typography 
                variant="subtitle1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}
              >
                {lugar}
              </Typography>
              {direccion && (
                <Typography 
                  variant="body2" 
                  color="textSecondary" 
                  gutterBottom
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  {direccion}
                </Typography>
              )}
              {mapEmbedLink && (
                <Box mt={2} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <iframe
                    src={mapEmbedLink}
                    width="100%" 
                    height={isMobile ? 150 : 200}
                    style={{ border: 0 }}
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Mapa del Evento">
                  </iframe>
                </Box>
              )}
            </Box>
            
            {/* Sección de Contacto - Adaptativa */}
            {contactos.length > 0 && (contactos[0]?.nombre || contactos[0]?.email || contactos[0]?.telefono) && (
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  p: { xs: 1.5, sm: 2 },
                }}
              >
                <Typography 
                  variant="h6" 
                  fontWeight="medium" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  <EmailIcon fontSize={isMobile ? "small" : "medium"} color="action" /> Contacto
                </Typography>
                <Divider sx={{ mb: 1.5 }}/>
                {contactos.map((contacto, index) => (
                  <Box key={index} mt={index > 0 ? 2 : 0}>
                    {contacto.nombre && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography 
                          variant="body2" 
                          color="textPrimary" 
                          fontWeight="medium"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {contacto.nombre}
                        </Typography>
                      </Box>
                    )}
                    {contacto.email && (
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <EmailIcon fontSize="small" color="action" />
                        <Link 
                          href={`mailto:${contacto.email}`} 
                          color="textSecondary" 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {contacto.email}
                        </Link>
                      </Box>
                    )}
                    {contacto.telefono && (
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Link 
                          href={`tel:${contacto.telefono}`} 
                          color="textSecondary" 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          {contacto.telefono}
                        </Link>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Columna Derecha (Tabs) - Adaptativa */}
          <Grid item xs={12} md={8} order={{ xs: 1, md: 2 }}>
            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider', 
              overflowX: 'auto', 
              bgcolor: 'background.paper', 
              borderRadius: '8px 8px 0 0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              '&::-webkit-scrollbar': { height: '4px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[400],
                borderRadius: '4px',
              }
            }}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons={isMobile ? "auto" : false}
                allowScrollButtonsMobile
                aria-label="Secciones del evento"
                sx={{
                  '& .MuiTabs-indicator': { backgroundColor: theme.palette.secondary.main },
                  '& .MuiTab-root': { 
                    textTransform: 'none', 
                    fontWeight: 'medium',
                    fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                    padding: { xs: '0.5rem 1rem', sm: '0.75rem 1.5rem', md: '1rem 2rem' },
                    minWidth: { xs: '80px', sm: '120px' }
                  },
                  '& .MuiTab-root.Mui-selected': { color: theme.palette.secondary.main },
                }}
              >
                <Tab label="Convocatoria" />
                {/* ** TAB DE REGISTRO ACTUALIZADA CON INDICADORES ** */}
                <Tab 
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {registrationTabConfig.label}
                      {registrationTabConfig.icon}
                      {!isMobile && (
                        <Chip
                          label={registrationTabConfig.chip.label}
                          color={registrationTabConfig.chip.color}
                          size="small"
                          variant="outlined"
                          sx={{
                            ml: 0.5,
                            height: 18,
                            fontSize: '0.65rem',
                            '& .MuiChip-label': {
                              px: 0.75,
                            }
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={registrationTabConfig.sx}
                />
                <Tab label="Resultados" />
                <Tab label="Live" />
              </Tabs>
            </Box>

            {/* Contenido de las Tabs */}
            <Box 
              p={{ xs: 1.5, sm: 2, md: 3 }} 
              sx={{ 
                bgcolor: 'background.paper', 
                borderRadius: '0 0 8px 8px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                minHeight: { xs: '300px', md: '400px' }
              }}
            >
              {selectedTab === 0 && <Convocatoria competencia={competencia} />}
              {selectedTab === 1 && <RegistroForm competencia={competencia} />}
              {selectedTab === 2 && (
                <EventProvider competitionId={competitionId}>
                  <EventsTable competencia={competencia} />
                </EventProvider>
              )}
              {selectedTab === 3 && (
                <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Live no disponible.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default EventPage;